import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { FormSection } from '@/components';

interface MessageTypeSelectorProps {
  messageType: 'groupChat' | 'singleChat' | 'sms';
  onMessageTypeChange: (type: 'groupChat' | 'singleChat' | 'sms') => void;
  recipientCount: number;
}

interface MessageTypeOption {
  value: 'groupChat' | 'singleChat' | 'sms';
  label: string;
  description: string;
  icon: keyof typeof SVG_COMPONENTS;
}

export const MessageTypeSelector: React.FC<MessageTypeSelectorProps> = ({
  messageType,
  onMessageTypeChange,
  recipientCount,
}) => {
  const messageTypeOptions: MessageTypeOption[] = [
    {
      value: 'singleChat',
      label: 'Conversation per recipient',
      description: `${recipientCount} conversations will be created, residents will not see each other responses`,
      icon: 'GROUP',
    },
    {
      value: 'sms',
      label: 'Text message thread',
      description: `${recipientCount} conversations will be created, residents will receive messages individually via SMS`,
      icon: 'MESSAGE_BLAST',
    },
    {
      value: 'groupChat',
      label: 'Group chat',
      description: 'One conversation will be created where all recipients can see each other responses',
      icon: 'EMPTY_MESSAGE',
    },
  ];

  const renderMessageTypeOption = (option: MessageTypeOption) => {
    const isSelected = messageType === option.value;
    const IconComponent = SVG_COMPONENTS[option.icon];

    return (
      <TouchableOpacity
        key={option.value}
        style={[
          styles.optionCard,
          isSelected && styles.optionCardSelected,
        ]}
        onPress={() => onMessageTypeChange(option.value)}
        activeOpacity={0.7}
      >
        <View style={styles.optionContent}>
          <View style={styles.optionHeader}>
            <View style={[
              styles.iconContainer,
              isSelected && styles.iconContainerSelected,
            ]}>
              <IconComponent
                width={20}
                height={20}
                color={isSelected ? ADMIN_COLORS.PRIMARY : ADMIN_COLORS.TEXT_SECONDARY}
              />
            </View>
            
            <View style={styles.optionTextContainer}>
              <Text style={[
                styles.optionLabel,
                isSelected && styles.optionLabelSelected,
              ]}>
                {option.label}
              </Text>
            </View>

            <View style={[
              styles.radioButton,
              isSelected && styles.radioButtonSelected,
            ]}>
              {isSelected && (
                <View style={styles.radioButtonInner} />
              )}
            </View>
          </View>
          
          <Text style={[
            styles.optionDescription,
            isSelected && styles.optionDescriptionSelected,
          ]}>
            {option.description}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <FormSection title="Message Type">
      <View style={styles.container}>
        <Text style={styles.subtitle}>
          Choose how you want to send this message to recipients
        </Text>
        
        <View style={styles.optionsContainer}>
          {messageTypeOptions.map(renderMessageTypeOption)}
        </View>
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  subtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 16,
    lineHeight: 20,
  },
  optionsContainer: {
    gap: 12,
  },
  optionCard: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    padding: 16,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  optionCardSelected: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '10', // 10% opacity
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  optionContent: {
    flex: 1,
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  iconContainerSelected: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '20', // 20% opacity
  },
  optionTextContainer: {
    flex: 1,
  },
  optionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  optionLabelSelected: {
    color: ADMIN_COLORS.PRIMARY,
  },
  optionDescription: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 18,
    marginLeft: 52, // Align with text after icon
  },
  optionDescriptionSelected: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  radioButtonSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
});